import { useCallback } from "react";
import type { Permission } from "@dentacore/shared-types";

import { useAuth } from "./auth-context";
import { useBookingLocation } from "../calendar/booking-location";

/**
 * Whether the signed-in person holds a permission (STORY-166).
 *
 * Read from `AuthUser.permissions`, which the server works out from their
 * roles and overrides each time it hands the user over. **This decides what
 * the screen draws and nothing else** — every route checks for itself, so a
 * stale copy can only ever show a control that then explains why it is
 * refused, never let somebody through (S3's rule, kept).
 */
export function useCan(): (permission: Permission) => boolean {
  const { user } = useAuth();
  // **Inside a booking's panel on the combined calendar**, what that booking's
  // own location allows (CF-D9) — not what the active location does.
  const at = useBookingLocation();
  const held = at?.permissions ?? user?.permissions;
  return useCallback((permission: Permission) => held?.includes(permission) === true, [held]);
}

/** Holds at least one of these. For a screen that several permissions open. */
export function useCanAny(): (permissions: readonly Permission[]) => boolean {
  const can = useCan();
  return useCallback((permissions: readonly Permission[]) => permissions.some(can), [can]);
}

/**
 * Everything that opens System (Settings). A person who holds none of these
 * has no System entry at all, as S3 settled for non-admins.
 */
export const SYSTEM_PERMISSIONS: readonly Permission[] = [
  "settings.clinic.manage",
  "settings.locations.manage",
  "settings.integrations.manage",
  "people.manage",
  "access.roles.manage",
  "access.assign",
];
